import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { format } from 'date-fns';
import { Business_Profile_Enum, Order_Status_Enum } from '@kheops/graphql';
import { convertSqlTimeToDate, getFormattedHour } from '@kheops/utils';
import { currentContextAtom } from '../../state';
import { orderAtom } from '../state/state';
import { longDateFormat } from '../../common/state/state';
import { isDeliveryDay } from '../../common/utils/date.utils';
import OrderDeliveryDateButton from './OrderDeliveryDateButton';
import { DeliveryDateOrderStatus } from './OrderDeliveryDateDialog';

const NotDeliveredYetStatuses = [Order_Status_Enum.OrderToBeValidatedByBuyer, Order_Status_Enum.OrderToBeValidatedBySupplier, Order_Status_Enum.OrderToBeDeliveredBySupplier, Order_Status_Enum.OrderToBePreparedBySupplier];

export default function OrderDeliveryDateForm(): React.JSX.Element {
  const { t } = useTranslation(['order', 'common']);
  const { realm } = useAtomValue(currentContextAtom);
  const order = useAtomValue(orderAtom);
  const [deliveryDate, setDeliveryDate] = useState<Date>();

  const canEditDeliveryDate = useMemo(() => {
    return (
      order.status === Order_Status_Enum.OrderToBeValidatedBySupplier
      || order.status === Order_Status_Enum.OrderToBeValidatedByBuyer
      || order.status === Order_Status_Enum.OrderToBeDeliveredBySupplier
      || order.status === Order_Status_Enum.OrderToBePreparedBySupplier
    ) && realm === Business_Profile_Enum.Supplier;
  }, [order]);

  const formattedDeliveryDate = useMemo((): string | undefined => {
    if (!deliveryDate) {
      return;
    }

    let result = format(deliveryDate, longDateFormat);

    if (isDeliveryDay(deliveryDate.getDay(), order.contract.buying_company.delivery_hours)) {
      const hoursDisplay = t(
        'order:between_hours',
        {
          openingHour: getFormattedHour(convertSqlTimeToDate(order.contract.buying_company.delivery_hours[0].opening_hour)),
          closingHour: getFormattedHour(convertSqlTimeToDate(order.contract.buying_company.delivery_hours[0].closing_hour)),
        },
      );

      result += ` ${hoursDisplay}`;
    }

    return result;
  }, [deliveryDate, order]);

  const dateComponent = useMemo(() => {
    if (!canEditDeliveryDate) {
      let message: string;

      if (!formattedDeliveryDate) {
        message = t('order:to_be_defined_by_the_supplier');
      } else {
        message = formattedDeliveryDate;
      }

      return (
        <Typography variant="bodyMedium">{message}</Typography>
      );
    }

    if (!formattedDeliveryDate) {
      return (
        <OrderDeliveryDateButton
          orderId={order.id}
          orderReferenceId={order.reference_id}
          orderStatus={order.status as DeliveryDateOrderStatus}
          mode="insert"
          orderShouldBeRescheduled={order.status === Order_Status_Enum.OrderToBeDeliveredBySupplier && !order.delivery_date}
        />
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="bodyMedium">{formattedDeliveryDate}</Typography>
        <OrderDeliveryDateButton
          orderId={order.id}
          orderReferenceId={order.reference_id}
          orderStatus={order.status as DeliveryDateOrderStatus}
          mode="edit"
          useEditIcon
          orderShouldBeRescheduled={order.status === Order_Status_Enum.OrderToBeDeliveredBySupplier && !order.delivery_date}
        />
      </Box>
    );
  }, [canEditDeliveryDate, deliveryDate, order, formattedDeliveryDate]);

  const deliveryDateLabel = useMemo(() => {
    let dateLabel = '';

    if (!!deliveryDate && order.status === Order_Status_Enum.OrderToBeValidatedBySupplier) {
      dateLabel = t('order:proposed_delivery');
    } else if (NotDeliveredYetStatuses.includes(order.status)) {
      dateLabel = t('order:to_deliver_on');
    } else {
      dateLabel = t('order:delivered_on');
    }

    return dateLabel;
  }, [deliveryDate, order]);

  useEffect((): void => {
    if (order.delivery_date !== undefined) {
      setDeliveryDate(order.delivery_date ? new Date(order.delivery_date) : undefined);
    }
  }, [order.delivery_date]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexDirection: canEditDeliveryDate ? 'column' : 'row',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="bodySmall" color="secondary" sx={{ mb: 0.5 }}>
          {deliveryDateLabel}
        </Typography>
        {dateComponent}
      </Box>
    </Box>
  );
}
